import * as React from 'react';
import { observer } from 'mobx-react';
import {
  Profile,
  ProfileType,
  PresenceState,
  UserProfileEntryProps,
  DeviceEntryProps,
  canImpersonate,
} from '@ixicare/resources';
import { Button, IconButton, Search, Table } from '@ixicare/ui';
import { LogIn, SquarePen, Trash2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { t } from '~i18n';
import { useStores } from '~store/utils';
import { flattenOrganizations, useOrganizations } from '~/modules/sites/hooks/useOrganizations';
import { GenericTable } from '../DeviceTables/GenericTable';
import { useSortableQuery } from '../DeviceTables/hooks/useSortableQuery';
import { useTableRowActions } from '../DeviceTables/hooks/useTableRowActions';
import { useOnReachBottom } from '../DeviceTables/utils';
import { DeviceSearchModal } from '~components/DeviceTables/DeviceSearchModal';
import { DeviceLinkModal } from '~components/DeviceTables/DeviceLinkModal';
import { DeviceCreateModal, ArchivedDeviceInfo } from '~components/DeviceTables/DeviceCreateModal';

import { ProfileTableConfig } from './tableConfig';
import { ProfileTransformContext } from './rowTransformers';
import styles from './ProfileTable.module.css';

// Presence sort is client-side: presence state lives in the MobX store, not on
// the profile entry, so useSortableQuery's sortAccessors can't reach it.
const PRESENCE_SORT_KEY = 'presence_state';
const PRESENCE_STATE_ORDER: Record<PresenceState, number> = {
  [PresenceState.Online]: 0,
  [PresenceState.Occupied]: 1,
  [PresenceState.Unavailable]: 2,
  [PresenceState.Offline]: 3,
};

interface Props {
  config: ProfileTableConfig;
  profileType: ProfileType;
  profileTypes?: ProfileType[];
  enableBatteryLegend?: boolean;
  editPermissions?: boolean;
  deletePermissions?: boolean;
  loginAsPermissions?: boolean;
}

export const ProfileTable = observer(function ProfileTable({
  config,
  profileType,
  profileTypes,
  enableBatteryLegend,
  editPermissions,
  deletePermissions,
  loginAsPermissions,
}: Props) {
  const { sites, auth, confirmation, permissions, presence } = useStores();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const requiresSiteFilter = permissions.has('can_view_site');
  const siteIdFilter = requiresSiteFilter ? (sites.selectedSite?.id ?? undefined) : undefined;

  const effectiveTypes: ProfileType[] = profileTypes?.length ? profileTypes : [profileType];
  const effectiveTypesKey = effectiveTypes.join(',');

  const [searchInput, setSearchInput] = React.useState('');
  const search = searchInput.trim();
  const [showLegend, setShowLegend] = React.useState(false);

  const [deviceSearchResident, setDeviceSearchResident] = React.useState<UserProfileEntryProps | null>(null);
  const [deviceLinkData, setDeviceLinkData] = React.useState<{
    device: DeviceEntryProps;
    resident: UserProfileEntryProps;
  } | null>(null);
  const [deviceCreateData, setDeviceCreateData] = React.useState<{
    archivedDevice: ArchivedDeviceInfo;
    resident: UserProfileEntryProps;
  } | null>(null);

  const queryFn = React.useCallback(
    ({
      page,
      perPage,
      orderBy,
      order,
    }: {
      page: number;
      perPage: number;
      orderBy?: string;
      order?: 'asc' | 'desc';
    }) => {
      const typeArgs =
        effectiveTypes.length === 1 ? { profileType: effectiveTypes[0] } : { profileTypes: effectiveTypes };
      if (search) {
        return Profile.getProfiles({ page, perPage, ...typeArgs, search, siteId: siteIdFilter });
      }
      return Profile.getProfiles({ page, perPage, orderBy, order, ...typeArgs, siteId: siteIdFilter });
    },
    // effectiveTypes is recomputed every render; effectiveTypesKey captures it.
    [effectiveTypesKey, search, siteIdFilter]
  );

  const organizationsQuery = useOrganizations();
  const organizationNameById = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const { node } of flattenOrganizations(organizationsQuery.data ?? [])) {
      map[node.id] = node.name;
    }
    return map;
  }, [organizationsQuery.data]);

  const orgNameOf = React.useCallback(
    (p: UserProfileEntryProps) =>
      (p.permissionOrganizationId && organizationNameById[p.permissionOrganizationId]) || null,
    [organizationNameById]
  );

  // permission_org sorts client-side (case-insensitive) once the table is in
  // 'all' mode; export uses the proper-case name.
  const sortAccessors = React.useMemo(
    () => ({
      ...config.sortAccessors,
      permission_org: (p: UserProfileEntryProps) => orgNameOf(p)?.toLowerCase() ?? null,
    }),
    [config.sortAccessors, orgNameOf]
  );
  const csvAccessors = React.useMemo(
    () => ({ ...sortAccessors, ...config.csvAccessors, permission_org: orgNameOf }),
    [sortAccessors, config.csvAccessors, orgNameOf]
  );

  const {
    sortedData,
    orderBy,
    ascOrder,
    mode,
    handleSort,
    refetch,
    isRefreshing,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    error,
    loadAll,
  } = useSortableQuery<UserProfileEntryProps>({
    queryKey: config.tableId,
    queryFn,
    scope: [effectiveTypesKey, search, siteIdFilter],
    enabled: !requiresSiteFilter || siteIdFilter != null,
    defaultOrderBy: config.defaultOrderBy ?? 'created',
    defaultAscOrder: config.defaultAscOrder,
    sortAccessors,
  });

  useOnReachBottom(fetchNextPage, mode === 'paginated' && !isRefreshing);

  // Bulk presence fetch for non-resident rows.
  const presenceSiteUuid = sites.selectedSiteUuid ?? undefined;
  React.useEffect(() => {
    const ids = sortedData.filter((r) => r.profileType !== ProfileType.Resident).map((r) => r.id);
    if (ids.length === 0) return;
    void presence.loadBulkPresence(ids, presenceSiteUuid);
  }, [sortedData, presenceSiteUuid, presence]);

  // Client-side presence sort.
  const finalData = React.useMemo(() => {
    if (orderBy !== PRESENCE_SORT_KEY) return sortedData;
    return [...sortedData].sort((a, b) => {
      const aRank = PRESENCE_STATE_ORDER[presence.bulk[a.id]?.state ?? PresenceState.Offline];
      const bRank = PRESENCE_STATE_ORDER[presence.bulk[b.id]?.state ?? PresenceState.Offline];
      const diff = aRank - bRank;
      return ascOrder ? diff : -diff;
    });
  }, [sortedData, orderBy, ascOrder, presence.bulk]);

  const handleDelete = (profile: UserProfileEntryProps) => {
    confirmation.show({
      title:
        profile.profileType === ProfileType.Resident
          ? t('residentDetail.confirmDelete.title')
          : t('healthcareProviderDetail.confirmDelete.title'),
      message:
        profile.profileType === ProfileType.Resident
          ? t('residentDetail.confirmDelete.message')
          : t('healthcareProviderDetail.confirmDelete.message'),
      onConfirm: () => {
        Profile.archiveProfile(profile.uuid)
          .then(() => refetch())
          .catch((e) => {
            console.error(e);
            refetch();
          });
      },
      onCancel: () => {},
    });
  };

  const showLoginAs = !!loginAsPermissions && !auth.isImpersonating;
  // Impersonators may not edit/delete non-resident profiles. Rather than
  // showing a lock icon (the old UX), we simply hide the actions — the
  // tooltip on impersonation in the topbar already explains the restriction.
  const isImpersonationLocked = (p: UserProfileEntryProps) =>
    !!auth.isImpersonating && p.profileType !== ProfileType.Resident;

  const canEdit = (p: UserProfileEntryProps) => !!editPermissions || p.id === auth.user?.id;

  const { actionProps, onActionClick, isActionVisible } = useTableRowActions<UserProfileEntryProps>(finalData, [
    {
      icon: 'edit',
      lucide: SquarePen,
      label: t('residents.table.edit'),
      onClick: (selectedProfiles) => {
        navigate(
          selectedProfiles[0].profileType === ProfileType.Resident
            ? `/residents/${selectedProfiles[0].id}/edit`
            : `/healthcare-providers/${selectedProfiles[0].id}/edit`
        );
      },
      isVisible: (p) => !isImpersonationLocked(p) && canEdit(p),
    },
    {
      icon: 'delete',
      lucide: Trash2,
      label: t('residents.table.delete'),
      onClick: (selectedProfiles) => {
        handleDelete(selectedProfiles[0]);
      },
      isVisible: (p) => !isImpersonationLocked(p) && !!deletePermissions,
    },
    {
      icon: 'login-as',
      lucide: LogIn,
      label: t('user.actions.loginAs'),
      onClick: async (selectedProfiles) => {
        const ok = await auth.loginAs(selectedProfiles[0].id);
        if (ok) {
          navigate('/dashboard');
        }
      },
      isVisible: (p) => showLoginAs && canImpersonate(auth.user, p),
    },
  ]);

  const transformContext: ProfileTransformContext = {
    presence,
    editPermissions,
    canViewDevices: requiresSiteFilter,
    organizationNameById,
    onRequestDeviceLink: setDeviceSearchResident,
  };

  // presence.bulk is observable; including it as a dep makes the StatusBadge
  // elements in the row array reflect bulk-presence updates without remount.
  const rows = React.useMemo(
    () => finalData.map((p) => config.transformRow(p, transformContext)),
    [finalData, presence.bulk, editPermissions, requiresSiteFilter, organizationNameById]
  );

  // While searching, the server orders by relevance, not by orderBy — hide the
  // sort chevron so it doesn't claim a sort that isn't applied.
  const effectiveOrderBy = search ? undefined : orderBy;

  const showLoadMore = mode === 'paginated' && hasNextPage && !error;

  const handleDeviceFound = (device: DeviceEntryProps) => {
    if (!deviceSearchResident) return;
    setDeviceLinkData({ device, resident: deviceSearchResident });
    setDeviceSearchResident(null);
  };

  const handleArchivedDeviceFound = (info: ArchivedDeviceInfo) => {
    if (!deviceSearchResident) return;
    setDeviceCreateData({ archivedDevice: info, resident: deviceSearchResident });
    setDeviceSearchResident(null);
  };

  const handleDeviceCreated = (device: DeviceEntryProps) => {
    const resident = deviceCreateData?.resident;
    setDeviceCreateData(null);
    if (resident) setDeviceLinkData({ device, resident });
  };

  const handleDeviceLinked = () => {
    setDeviceLinkData(null);
    queryClient.invalidateQueries({ queryKey: [config.tableId] });
    refetch();
  };

  return (
    <>
      <div className={styles.Header}>
        <div className={styles.SearchContainer}>
          <Search value={searchInput} onSearchChange={setSearchInput} placeholder={t('profiles.filters.search')} />
        </div>
        {enableBatteryLegend && (
          <div className={styles.LegendBtnContainer}>
            <button onClick={() => setShowLegend(!showLegend)}>{t('legend.overlay.title').toLowerCase()}</button>
          </div>
        )}
      </div>

      {showLegend && (
        <section className={styles.LegendContainer}>
          <Table.Head className={styles.LegendHeader}>
            <Table.Row>
              <Table.HeadCol>
                <p>
                  <IconButton icon="cross" />
                </p>
              </Table.HeadCol>
              <Table.HeadCol>
                <p>*!</p>
              </Table.HeadCol>
              <Table.HeadCol>
                <p>Last Seen</p>
              </Table.HeadCol>
            </Table.Row>
          </Table.Head>
          <Table.Body className={styles.LegendBody}>
            <Table.Row>
              <Table.Col>
                <p>{t('profiles.legend.notAvailable.description')}</p>
              </Table.Col>
              <Table.Col>
                <p>{t('profiles.legend.brokenBattery.description')}</p>
              </Table.Col>
              <Table.Col>
                <p>{t('profiles.legend.lastSeen.description')}</p>
              </Table.Col>
            </Table.Row>
          </Table.Body>
        </section>
      )}

      <GenericTable
        config={config}
        orderBy={effectiveOrderBy}
        asc={ascOrder}
        rows={rows}
        onClickColumn={handleSort}
        actions={actionProps}
        onActionClick={onActionClick}
        isActionVisible={isActionVisible}
        getRowLabel={(i) => {
          const p = finalData[i];
          return p ? `${p.firstName} ${p.lastName}` : '';
        }}
        getRowKey={(i) => String(finalData[i]?.id ?? i)}
        onRefresh={refetch}
        isRefreshing={isRefreshing}
        csvData={sortedData}
        csvAccessors={csvAccessors}
        onPrepareExport={loadAll}
      />

      {showLoadMore && (
        <Button loading={isFetchingNextPage} onClick={() => fetchNextPage()}>
          Load more
        </Button>
      )}

      {deviceSearchResident && (
        <DeviceSearchModal
          onClose={() => setDeviceSearchResident(null)}
          onDeviceFound={handleDeviceFound}
          onArchivedDeviceFound={handleArchivedDeviceFound}
        />
      )}
      {deviceLinkData && (
        <DeviceLinkModal
          device={deviceLinkData.device}
          newOwnerId={deviceLinkData.resident.id}
          newOwnerName={`${deviceLinkData.resident.firstName} ${deviceLinkData.resident.lastName}`}
          onClose={() => setDeviceLinkData(null)}
          onLinked={handleDeviceLinked}
        />
      )}
      {deviceCreateData && (
        <DeviceCreateModal
          archivedDevice={deviceCreateData.archivedDevice}
          onClose={() => setDeviceCreateData(null)}
          onCreated={handleDeviceCreated}
        />
      )}
    </>
  );
});
